import React from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PageLayout from "./layouts/PageLayout";
import Section from "./layouts/Section";
import areasData from "../data/areasData";

const services = [
  { name: "Screen Repair", slug: "screen-repair", icon: "fas fa-mobile-alt" },
  { name: "Battery Replacement", slug: "battery-replacement", icon: "fas fa-battery-full" },
  { name: "Water Damage Repair", slug: "water-damage-repair", icon: "fas fa-tint" },
  { name: "Charging Port Repair", slug: "charging-port-repair", icon: "fas fa-plug" },
  { name: "Data Recovery", slug: "data-recovery", icon: "fas fa-database" },
  { name: "Software & OS Issues", slug: "software-issues", icon: "fas fa-cog" },
  { name: "PC & Laptop Repair", slug: null, icon: "fas fa-laptop" },
  { name: "Mac Repair", slug: null, icon: "fab fa-apple" },
];

const AreaPage = () => {
  const { slug } = useParams();
  const area = areasData[slug];

  if (!area) {
    return (
      <PageLayout title="Area Not Found | Cell Station">
        <Section title="Area Not Found">
          <div className="section">
            <div className="container text-center" style={{ padding: "60px 0" }}>
              <h2 className="mb-20">Area Not Found</h2>
              <p className="text-muted mb-25">
                Sorry, we couldn't find that location page.
              </p>
              <Link to="/" className="btn btn-primary">
                Go to Homepage
              </Link>
            </div>
          </div>
        </Section>
      </PageLayout>
    );
  }

  const canonical = `https://www.icellstation.com/areas/${area.slug}`;

  const localBusinessJsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "Cell Station",
    description: `Cell phone and computer repair services near ${area.name}, ${area.state}. Same-day service available.`,
    telephone: "+1-215-939-3933",
    email: "icellstation1@gmail.com",
    url: "https://www.icellstation.com",
    address: {
      "@type": "PostalAddress",
      streetAddress: "2500 W Moreland Rd, Space #1077",
      addressLocality: "Willow Grove",
      addressRegion: "PA",
      postalCode: "19090",
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: "40.1320",
      longitude: "-75.1260",
    },
    areaServed: {
      "@type": "City",
      name: area.name,
      containedInPlace: {
        "@type": "State",
        name: "Pennsylvania",
      },
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday"],
        opens: "10:00",
        closes: "20:00",
      },
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Friday", "Saturday"],
        opens: "10:00",
        closes: "21:00",
      },
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: "Sunday",
        opens: "10:00",
        closes: "20:00",
      },
    ],
  };

  return (
    <PageLayout
      title={area.metaTitle}
      description={area.metaDescription}
      url={canonical}
    >
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(localBusinessJsonLd)}
        </script>
      </Helmet>

      <Section title={`Phone Repair Near ${area.name}`}>
        <div className="section">
          <div className="container">
            <div className="row">
              {/* Main Content */}
              <div className="col-lg-8">
                {/* Hero Banner */}
                <div
                  style={{
                    background: "linear-gradient(135deg, #18305d 0%, #0f1d3a 100%)",
                    borderRadius: "16px",
                    padding: "40px",
                    marginBottom: "30px",
                    color: "#ffffff",
                  }}
                >
                  <h1 style={{ fontSize: "2rem", marginBottom: "10px" }}>
                    Cell Phone Repair Near {area.name}, {area.state}
                  </h1>
                  <p style={{ fontSize: "1.1rem", opacity: 0.9, margin: 0 }}>
                    Just{" "}
                    <strong style={{ color: "#ffaa19" }}>{area.distance}</strong>{" "}
                    away — about a{" "}
                    <strong style={{ color: "#ffaa19" }}>{area.driveTime}</strong>{" "}
                    drive from {area.name}
                  </p>
                </div>

                {/* About */}
                <div style={{ marginBottom: "30px" }}>
                  <h2 className="h4 text-dark mb-15">
                    Serving {area.name} Residents
                  </h2>
                  <p className="text-muted" style={{ lineHeight: "1.8" }}>
                    {area.description}
                  </p>
                  <p className="text-muted" style={{ lineHeight: "1.8" }}>
                    <i className="fas fa-directions text-primary mr-2" />
                    {area.landmarks}
                  </p>
                </div>

                {/* Services Available */}
                <div
                  style={{
                    background: "#f8f9fa",
                    borderRadius: "12px",
                    padding: "30px",
                    marginBottom: "30px",
                  }}
                >
                  <h2 className="h4 text-dark mb-20">
                    Services Available for {area.name} Customers
                  </h2>
                  <div className="row">
                    {services.map((svc, index) => (
                      <div key={index} className="col-md-6 mb-15">
                        {svc.slug ? (
                          <Link
                            to={`/services/${svc.slug}`}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              color: "#333",
                              padding: "12px 15px",
                              background: "#ffffff",
                              borderRadius: "8px",
                              transition: "box-shadow 0.2s",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                            }}
                          >
                            <i
                              className={svc.icon}
                              style={{
                                color: "#F50963",
                                width: "25px",
                                fontSize: "1.1rem",
                              }}
                            />
                            <span style={{ marginLeft: "10px", fontWeight: "500" }}>
                              {svc.name}
                            </span>
                            <i
                              className="fas fa-chevron-right"
                              style={{ marginLeft: "auto", fontSize: "0.75rem", color: "#ccc" }}
                            />
                          </Link>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              color: "#333",
                              padding: "12px 15px",
                              background: "#ffffff",
                              borderRadius: "8px",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                            }}
                          >
                            <i
                              className={svc.icon}
                              style={{
                                color: "#F50963",
                                width: "25px",
                                fontSize: "1.1rem",
                              }}
                            />
                            <span style={{ marginLeft: "10px", fontWeight: "500" }}>
                              {svc.name}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Why Choose Us */}
                <div style={{ marginBottom: "30px" }}>
                  <h2 className="h4 text-dark mb-20">
                    Why {area.name} Residents Choose Cell Station
                  </h2>
                  <div className="row">
                    {[
                      { icon: "fas fa-clock", title: "Same-Day Service", text: "Most repairs completed while you wait" },
                      { icon: "fas fa-shield-alt", title: "30-Day Warranty", text: "Every repair backed by our warranty" },
                      { icon: "fas fa-star", title: "4.7/5 Google Rating", text: "163+ five-star reviews from happy customers" },
                      { icon: "fas fa-award", title: "19+ Years Experience", text: "Trusted since 2006 in Willow Grove, PA" },
                    ].map((item, index) => (
                      <div key={index} className="col-md-6 mb-20">
                        <div style={{ display: "flex", alignItems: "flex-start" }}>
                          <i
                            className={item.icon}
                            style={{
                              color: "#ffaa19",
                              fontSize: "1.3rem",
                              marginTop: "3px",
                              width: "30px",
                            }}
                          />
                          <div style={{ marginLeft: "10px" }}>
                            <h4 style={{ fontSize: "1rem", fontWeight: "600", marginBottom: "4px" }}>
                              {item.title}
                            </h4>
                            <p className="text-muted" style={{ margin: 0, fontSize: "0.9rem" }}>
                              {item.text}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Map */}
                <div style={{ marginBottom: "30px" }}>
                  <h2 className="h4 text-dark mb-15">Find Us</h2>
                  <iframe
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1112.526522562257!2d-75.12602792321633!3d40.13197566201095!2m3!1f0!2f39.121133786135466!3f0!3m2!1i1024!2i768!4f35!3m3!1m2!1s0x89c6b1fc42ab6fef%3A0x7c78b804e3a9c5ec!2sCell%20Station!5e1!3m2!1sen!2sus!4v1730661192650!5m2!1sen!2sus"
                    width="100%"
                    height="350"
                    title={`Cell Station - Near ${area.name}, ${area.state}`}
                    style={{ border: 0, borderRadius: "12px" }}
                    allowFullScreen
                  />
                </div>
              </div>

              {/* Sidebar */}
              <div className="col-lg-4">
                {/* Contact Card */}
                <div
                  style={{
                    background: "#ffffff",
                    borderRadius: "12px",
                    padding: "30px",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    marginBottom: "25px",
                    position: "sticky",
                    top: "20px",
                  }}
                >
                  <h3 className="h5 text-dark mb-15">
                    <i className="fas fa-map-marker-alt text-primary mr-2" />
                    Visit Us
                  </h3>
                  <p className="text-muted" style={{ fontSize: "0.95rem" }}>
                    2500 W Moreland Rd, Space #1077
                    <br />
                    Willow Grove, PA 19090
                  </p>
                  <p
                    style={{
                      background: "#f0f7ff",
                      padding: "10px 15px",
                      borderRadius: "8px",
                      fontSize: "0.9rem",
                      color: "#18305d",
                      fontWeight: "500",
                    }}
                  >
                    <i className="fas fa-car mr-2" />
                    {area.driveTime} drive from {area.name}
                  </p>

                  <h4 className="h6 text-dark mb-10 mt-20">Store Hours</h4>
                  <div style={{ fontSize: "0.9rem", color: "#666" }}>
                    <div className="d-flex justify-content-between mb-1">
                      <span>Mon - Thu:</span>
                      <span>10AM - 8PM</span>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span>Fri - Sat:</span>
                      <span>10AM - 9PM</span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>Sunday:</span>
                      <span>10AM - 8PM</span>
                    </div>
                  </div>

                  <hr style={{ margin: "20px 0" }} />

                  <a
                    href="tel:+12159393933"
                    className="btn btn-primary w-100 mb-10"
                    style={{ padding: "14px 20px" }}
                  >
                    <i className="fas fa-phone-alt mr-2" />
                    Call (215) 939-3933
                  </a>

                  <Link
                    to="/contact-us"
                    className="btn w-100"
                    style={{
                      padding: "14px 20px",
                      background: "#18305d",
                      color: "#ffffff",
                      borderRadius: "5px",
                    }}
                  >
                    <i className="fas fa-comment-dots mr-2" />
                    Get a Free Quote
                  </Link>
                </div>

                {/* Other Areas */}
                <div
                  style={{
                    background: "#f8f9fa",
                    borderRadius: "12px",
                    padding: "25px",
                  }}
                >
                  <h4 className="h5 text-dark mb-15">We Also Serve</h4>
                  <ul className="list-unstyled">
                    {Object.values(areasData)
                      .filter((a) => a.slug !== area.slug)
                      .map((a) => (
                        <li key={a.slug} style={{ marginBottom: "8px" }}>
                          <Link
                            to={`/areas/${a.slug}`}
                            style={{ color: "#4a4a4a" }}
                          >
                            <i
                              className="fas fa-map-marker-alt"
                              style={{
                                color: "#F50963",
                                width: "18px",
                                fontSize: "0.85rem",
                              }}
                            />
                            <span style={{ marginLeft: "8px" }}>
                              {a.name}, {a.state}
                            </span>
                            <span
                              className="text-muted"
                              style={{ fontSize: "0.8rem", marginLeft: "5px" }}
                            >
                              ({a.distance})
                            </span>
                          </Link>
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>
    </PageLayout>
  );
};

export default AreaPage;
